"use client"
import { useEffect, useRef, useState } from "react"
import { useMetricDetails } from "./metricDetailsContext"
import { METRIC_DETAILS } from "./metricDetailsData"
import { MetricDetailsContent } from "./MetricDetailsContent"
import {
  MetricPanelRedesign,
  type MetricPanelDesignId,
} from "./panel-redesigns/metricPanelRedesigns"
import type { MetricId } from "./metricDetailsTypes"

// Mobile-only surface for metric details. On desktop the open metric renders
// as an alternative HUD state in the side column (MetricDetailsHudState);
// mobile has no persistent column, so the same content appears as a
// screen-filling card instead. Mounted only while open (see
// MobileMetricDetailsPanel below), keyed by metric.
export const MetricDetailsPanel = ({
  metricId,
  year,
  panelDesign = null,
}: {
  metricId: MetricId
  // Current HUD year (fractional), synced to scroll — drives the now-dot.
  year: number
  // Same design switch as the desktop column (MetricDetailsHudState): a
  // redesign id renders MetricPanelRedesign, null the production content —
  // keep the two surfaces in sync.
  panelDesign?: MetricPanelDesignId | null
}) => {
  const { close } = useMetricDetails()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [closeVisible, setCloseVisible] = useState(true)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [close])

  // Keep the close button visible at the card top; once the reader scrolls
  // down, fade it out when the pointer goes idle and bring it back on scroll or
  // mouse move. Hover-capable pointers only: on touch there is no mousemove to
  // bring it back, so it stays put.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (!window.matchMedia("(hover: hover)").matches) return
    let hideTimer: ReturnType<typeof setTimeout> | undefined
    const bump = () => {
      setCloseVisible(true)
      if (hideTimer) clearTimeout(hideTimer)
      if (el.scrollTop > 4) hideTimer = setTimeout(() => setCloseVisible(false), 1800)
    }
    bump()
    el.addEventListener("scroll", bump, { passive: true })
    el.addEventListener("mousemove", bump)
    return () => {
      el.removeEventListener("scroll", bump)
      el.removeEventListener("mousemove", bump)
      if (hideTimer) clearTimeout(hideTimer)
    }
  }, [])

  // The card covers the viewport but isn't a separate document layer — lock
  // body scroll behind it so flicks past its edges don't move the story (and
  // with it the HUD year) underneath.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  const detail = METRIC_DETAILS[metricId]
  if (!detail) return null

  // Outer box is the positioned, non-scrolling frame (so the close button pins
  // to it and doesn't scroll away); the inner div is what actually scrolls.
  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={`${detail.label} — sources and details`}
      className="fixed inset-x-2 top-2 bottom-2 z-[9999] overflow-hidden rounded-[10px] border border-[#d9d8cc] bg-vivid-background text-[#111111]"
    >
      {/* Close button pinned to the frame — stays put as the card scrolls.
          Only for the stock content: the redesigned panel brings its own
          chip-chevron back control (same closer as desktop). */}
      {panelDesign === null && <button
        type="button"
        onClick={close}
        aria-label="Close details"
        className={`absolute right-[18px] top-[18px] z-20 h-[30px] w-[30px] rounded-[5px] border border-[#e4e3d6] bg-vivid-background text-[#111111] transition-opacity duration-300 hover:border-[var(--accent)] hover:text-[var(--accent)] ${closeVisible ? "opacity-100" : "pointer-events-none opacity-0"}`}
      >
        ✕
      </button>}
      <div ref={scrollRef} className="scrollbar-hide h-full overflow-auto overscroll-contain px-5 py-[18px]">
        {panelDesign ? (
          <MetricPanelRedesign
            designId={panelDesign}
            metricId={metricId}
            year={year}
            onClose={close}
            // This card has no overlay DetailsYearPill (that lives in the
            // desktop HUD shell) — the header's year renders as real text.
            staticYearPill
          />
        ) : (
          <MetricDetailsContent metricId={metricId} year={year} />
        )}
      </div>
    </div>
  )
}

// Mobile mount: reads the open metric itself and renders nothing when closed
// (no exit choreography on mobile).
export const MobileMetricDetailsPanel = ({
  year,
  panelDesign = null,
}: {
  year: number
  panelDesign?: MetricPanelDesignId | null
}) => {
  const { openId } = useMetricDetails()
  if (!openId) return null
  return (
    <MetricDetailsPanel
      key={openId}
      metricId={openId}
      year={year}
      panelDesign={panelDesign}
    />
  )
}
